export enum DocumentSource {
    PLAID = "plaid",
    RAMP = "ramp",
    MERCURY = "mercury",
    USER = "user",
    DIRECT = "direct",
    DEMO_ACCOUNT = "demoAccount",
    DEMO = "demo",
    RETOOL = "Retool",
    EXPORT = "export",
    MCP = "mcp",
    QUICKBOOKS = "quickbooks",
}

export const INTEGRATION_DOCUMENT_SOURCES: DocumentSource[] = [
    DocumentSource.PLAID,
    DocumentSource.RAMP,
    DocumentSource.MERCURY,
    DocumentSource.QUICKBOOKS,
];

const ENRICHMENT_EXEMPT_DOCUMENT_SOURCES: ReadonlySet<string> = new Set([
    DocumentSource.QUICKBOOKS,
]);

export function isEnrichmentExemptDocumentSource(source: string): boolean {
    return ENRICHMENT_EXEMPT_DOCUMENT_SOURCES.has(source);
}

export enum FileType {
    PDF = "pdf",
    CSV = "csv",
    XLSX = "xlsx",
    XLS = "xls",
    XLSM = "xlsm",
    JPG = "jpg",
    JPEG = "jpeg",
    PNG = "png",
    WORD = "word",
    ZIP = "zip",
}

export const FILE_TYPE_EXTENSIONS: Record<FileType, string> = {
    [FileType.PDF]: ".pdf",
    [FileType.CSV]: ".csv",
    [FileType.XLSX]: ".xlsx",
    [FileType.XLS]: ".xls",
    [FileType.XLSM]: ".xlsm",
    [FileType.JPG]: ".jpg",
    [FileType.JPEG]: ".jpeg",
    [FileType.PNG]: ".png",
    [FileType.WORD]: ".docx",
    [FileType.ZIP]: ".zip",
};

export const FILE_TYPE_NAMES: Record<FileType, string> = {
    [FileType.PDF]: "PDF",
    [FileType.CSV]: "CSV",
    [FileType.XLSX]: "XLSX",
    [FileType.XLS]: "XLS",
    [FileType.XLSM]: "XLSM",
    [FileType.JPG]: "JPG",
    [FileType.JPEG]: "JPEG",
    [FileType.PNG]: "PNG",
    [FileType.WORD]: "DOCX",
    [FileType.ZIP]: "ZIP",
};

// First entry is the canonical type matching the extension; any aliases follow.
export const FILE_TYPE_MIME_TYPES: Record<FileType, string[]> = {
    [FileType.PDF]: ["application/pdf"],
    [FileType.CSV]: ["text/csv"],
    [FileType.XLSX]: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    [FileType.XLS]: ["application/vnd.ms-excel"],
    [FileType.XLSM]: ["application/vnd.ms-excel.sheet.macroEnabled.12"],
    [FileType.JPG]: ["image/jpeg"],
    [FileType.JPEG]: ["image/jpeg"],
    [FileType.PNG]: ["image/png"],
    [FileType.WORD]: [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
    ],
    [FileType.ZIP]: ["application/zip"],
};

export enum FileTypeGroup {
    PDF = "PDF",
    CSV = "CSV",
    EXCEL = "Excel",
    WORD = "Word",
    JPG = "JPG",
    PNG = "PNG",
}

const ext = (fileType: FileType) => FILE_TYPE_EXTENSIONS[fileType].slice(1);

interface FileTypeGroupConfig {
    label: FileTypeGroup;
    extensions: string[];
}

export const FILE_TYPE_GROUPS: FileTypeGroupConfig[] = [
    {
        label: FileTypeGroup.PDF,
        extensions: [ext(FileType.PDF)],
    },
    {
        label: FileTypeGroup.CSV,
        extensions: [ext(FileType.CSV)],
    },
    {
        label: FileTypeGroup.EXCEL,
        extensions: [ext(FileType.XLS), ext(FileType.XLSX), ext(FileType.XLSM)],
    },
    {
        label: FileTypeGroup.WORD,
        extensions: [ext(FileType.WORD)],
    },
    {
        label: FileTypeGroup.JPG,
        extensions: [ext(FileType.JPG), ext(FileType.JPEG)],
    },
    {
        label: FileTypeGroup.PNG,
        extensions: [ext(FileType.PNG)],
    },
];

export const VALID_FILE_EXTENSIONS = Object.values(FILE_TYPE_EXTENSIONS).map(
    (e) => e.slice(1),
);

export const EXTENSION_TO_FILE_TYPE_GROUP = FILE_TYPE_GROUPS.reduce<
    Record<string, FileTypeGroup>
>((map, { label, extensions }) => {
    for (const extension of extensions) {
        map[extension] = label;
    }
    return map;
}, {});

export const RECEIPT_TYPES = [
    FileType.JPEG,
    FileType.PNG,
    FileType.PDF,
] as const;
export const SPREADSHEET_TYPES = [
    FileType.CSV,
    FileType.XLS,
    FileType.XLSX,
    FileType.XLSM,
] as const;

export const ARCHIVE_TYPES = FILE_TYPE_MIME_TYPES[FileType.ZIP];

export const FINANCIAL_DOCUMENT_FILE_TYPES = [
    ...RECEIPT_TYPES,
    ...SPREADSHEET_TYPES,
    FileType.WORD,
] as const;

export const FINANCIAL_DOCUMENT_TYPES = FINANCIAL_DOCUMENT_FILE_TYPES.flatMap(
    (type) => FILE_TYPE_MIME_TYPES[type],
);

export type FinancialDocumentType = (typeof FINANCIAL_DOCUMENT_TYPES)[number];

export const MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB = 20;
export const MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES =
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB * 1024 * 1024;

export const MAX_ARCHIVE_FILE_S3_SIZE_MB = 2048;
export const MAX_ARCHIVE_FILE_S3_SIZE_BYTES =
    MAX_ARCHIVE_FILE_S3_SIZE_MB * 1024 * 1024;

export const QBD_EXTRACTOR_JSON_CONTENT_TYPE = "application/json";
export const QBD_EXTRACTOR_JSON_TYPES = [QBD_EXTRACTOR_JSON_CONTENT_TYPE];
export const MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_MB = 250;
export const MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_BYTES =
    MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_MB * 1024 * 1024;

export const DOCUMENT_TAB_UPLOAD_MIME_TYPES = [
    ...FINANCIAL_DOCUMENT_TYPES,
    ...ARCHIVE_TYPES,
    ...QBD_EXTRACTOR_JSON_TYPES,
] as const;

export function isArchiveContentType(contentType: string): boolean {
    return ARCHIVE_TYPES.some((archiveType) => archiveType === contentType);
}

export function isQbdExtractorJsonUpload(file: {
    type: string;
    name: string;
}): boolean {
    return (
        file.type === QBD_EXTRACTOR_JSON_CONTENT_TYPE ||
        file.name.toLowerCase().endsWith(".json")
    );
}

export function isDirectS3UploadFile(file: {
    type: string;
    name: string;
}): boolean {
    return isArchiveContentType(file.type) || isQbdExtractorJsonUpload(file);
}

export function isAllowedDocumentTabUploadFileName(fileName: string): boolean {
    const normalizedName = fileName.toLowerCase();
    if (normalizedName.endsWith(".json")) {
        return true;
    }
    const extension = normalizedName.includes(".")
        ? normalizedName.slice(normalizedName.lastIndexOf(".") + 1)
        : "";
    return VALID_FILE_EXTENSIONS.some((allowed) => allowed === extension);
}

export function maxUploadSizeBytesForFile(file: {
    type: string;
    name: string;
}): number {
    if (isQbdExtractorJsonUpload(file)) {
        return MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_BYTES;
    }
    if (isArchiveContentType(file.type)) {
        return MAX_ARCHIVE_FILE_S3_SIZE_BYTES;
    }
    return MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES;
}

export enum RemovingDocumentErrorCode {
    REMOVING_DOCUMENT_WITH_TRANSACTIONS = "removing_document_with_transactions",
    REMOVING_DOCUMENT_USED_IN_RECONCILIATION = "removing_document_used_in_reconciliation",
}

export enum BulkActionErrorCode {
    INVALID_DOCUMENT_CATEGORY = "invalid_document_category",
    DOCUMENT_LINKED_TO_IMPORTED_ACCOUNT = "document_linked_to_imported_account",
}

export enum ChangeEntityErrorCode {
    DOCUMENT_LINKED_TO_IMPORTED_ACCOUNT = "document_linked_to_imported_account",
}

export const LINKED_ACCOUNT_DISABLED_REASON =
    "Can't change entity or financial account while document is linked to an imported account";
